import React, { useState, useEffect } from 'react';
import {
  Typography, Box, TextField, Button, CircularProgress, Alert, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Card, CardContent,
  Divider, Select, MenuItem, InputAdornment, IconButton
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useParams, useNavigate } from 'react-router-dom';
import api, { getUserEmail } from '../../api';
import { API_CONFIG } from '../../config';
import { useLanguage } from '../../i18n/LanguageContext';

interface UserDetail {
  email: string;
  name: string;
  status: string;
  is_admin: boolean;
  height?: number;
  date_of_birth?: string;
  gender?: string;
  target_weight?: number;
  target_date?: string;
  subscription_status?: string;
  stripe_customer_id?: string;
}

const statusColor = (s: string): 'success' | 'warning' | 'error' | 'default' => {
  if (s === 'active') return 'success';
  if (s === 'pending') return 'warning';
  if (s === 'suspended') return 'error';
  return 'default';
};

// Generates a random password matching PASSWORD_RE: uppercase, digit, special char, 12 chars
function generatePassword(): string {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const special = '!@#$%^&*(),.?';
  const all = upper + lower + digits + special;
  const rand = (chars: string) => chars[Math.floor(Math.random() * chars.length)];
  const core = Array.from({ length: 8 }, () => rand(all)).join('');
  return rand(upper) + rand(digits) + rand(special) + rand(lower) + core;
}

const AdminUserDetail: React.FC = () => {
  const { email } = useParams<{ email: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [refunding, setRefunding] = useState(false);

  // Editable fields
  const [name, setName] = useState('');
  const [height, setHeight] = useState('');
  const [targetWeight, setTargetWeight] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [targetDate, setTargetDate] = useState('');

  // Password reset dialog
  const [resetOpen, setResetOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resettingPw, setResettingPw] = useState(false);

  // Admin toggle dialog
  const [adminToggleOpen, setAdminToggleOpen] = useState(false);
  const [togglingAdmin, setTogglingAdmin] = useState(false);

  useEffect(() => {
    if (!email) return;
    api.get(API_CONFIG.endpoints.admin.user(decodeURIComponent(email)))
      .then(r => {
        setUser(r.data);
        setName(r.data.name || '');
        setHeight(r.data.height?.toString() || '');
        setTargetWeight(r.data.target_weight?.toString() || '');
        setDateOfBirth(r.data.date_of_birth || '');
        setGender(r.data.gender || '');
        setTargetDate(r.data.target_date || '');
      })
      .catch(() => setError(t.adminActionFailed))
      .finally(() => setLoading(false));
  }, [email, t.adminActionFailed]);

  const handleSave = async () => {
    if (!email) return;
    setSaving(true);
    try {
      await api.put(API_CONFIG.endpoints.admin.user(decodeURIComponent(email)), {
        name,
        height: parseFloat(height) || undefined,
        target_weight: parseFloat(targetWeight) || undefined,
        date_of_birth: dateOfBirth || undefined,
        gender: gender || undefined,
        target_date: targetDate || undefined,
      });
      setSuccess(t.profileUpdated);
    } catch { setError(t.adminActionFailed); }
    finally { setSaving(false); }
  };

  const handleAction = async (action: 'approve' | 'suspend' | 'reactivate') => {
    if (!email) return;
    try {
      const decoded = decodeURIComponent(email);
      if (action === 'approve') await api.post(API_CONFIG.endpoints.admin.approve(decoded));
      else if (action === 'suspend') await api.post(API_CONFIG.endpoints.admin.suspend(decoded));
      else await api.post(API_CONFIG.endpoints.admin.reactivate(decoded));
      const messages = { approve: t.userApproved, suspend: t.userSuspended, reactivate: t.userReactivated };
      setSuccess(messages[action]);
      const r = await api.get(API_CONFIG.endpoints.admin.user(decoded));
      setUser(r.data);
    } catch { setError(t.adminActionFailed); }
  };

  const handleDelete = async () => {
    if (!email) return;
    try {
      await api.delete(API_CONFIG.endpoints.admin.delete(decodeURIComponent(email)));
      setDeleteOpen(false);
      navigate('/admin/users');
    } catch { setError(t.adminActionFailed); }
  };

  const handleRefund = async () => {
    if (!email) return;
    setRefunding(true);
    try {
      await api.post(API_CONFIG.endpoints.admin.refund(decodeURIComponent(email)));
      setSuccess(t.refundIssued);
    } catch { setError(t.refundFailed); }
    finally { setRefunding(false); }
  };

  const handleResetPassword = async () => {
    if (!email) return;
    setResettingPw(true);
    try {
      await api.post(API_CONFIG.endpoints.admin.resetPassword(decodeURIComponent(email)), {
        new_password: newPassword,
      });
      setSuccess(t.passwordReset);
      setResetOpen(false);
      setNewPassword('');
    } catch { setError(t.passwordResetFailed); }
    finally { setResettingPw(false); }
  };

  const handleToggleAdmin = async () => {
    if (!email) return;
    setTogglingAdmin(true);
    try {
      await api.post(API_CONFIG.endpoints.admin.toggleAdmin(decodeURIComponent(email)));
      setSuccess(t.adminToggled);
      setAdminToggleOpen(false);
      const r = await api.get(API_CONFIG.endpoints.admin.user(decodeURIComponent(email)));
      setUser(r.data);
    } catch { setError(t.adminToggleFailed); }
    finally { setTogglingAdmin(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (!user) return <Alert severity="error">{t.adminActionFailed}</Alert>;

  const isSelf = getUserEmail() === decodeURIComponent(email ?? '');

  return (
    <Box maxWidth={600} mx="auto">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ flexGrow: 1 }}>
          {user.name}
        </Typography>
        <Chip
          label={t[`status${user.status.charAt(0).toUpperCase() + user.status.slice(1)}` as keyof typeof t] as string || user.status}
          color={statusColor(user.status)}
        />
        {user.is_admin && <Chip label={t.isAdmin} color="primary" />}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Card sx={{ mb: 2, borderRadius: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>{t.email}</Typography>
          <Typography sx={{ mb: 2 }}>{user.email}</Typography>

          <TextField fullWidth size="small" label={t.name} value={name}
            onChange={e => setName(e.target.value)} sx={{ mb: 2 }} />
          <TextField fullWidth size="small" label={t.height} type="number" value={height}
            onChange={e => setHeight(e.target.value)} sx={{ mb: 2 }} />
          <TextField fullWidth size="small" label={t.targetWeight} type="number" value={targetWeight}
            onChange={e => setTargetWeight(e.target.value)} sx={{ mb: 2 }} />
          <TextField fullWidth size="small" label={t.dateOfBirth} type="date"
            value={dateOfBirth} onChange={e => setDateOfBirth(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
          <Select fullWidth size="small" value={gender} onChange={e => setGender(e.target.value as string)}
            sx={{ mb: 2 }}>
            <MenuItem value="male">{t.male}</MenuItem>
            <MenuItem value="female">{t.female}</MenuItem>
            <MenuItem value="other">{t.other}</MenuItem>
          </Select>
          <TextField fullWidth size="small" label={t.targetDate} type="date"
            value={targetDate} onChange={e => setTargetDate(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />

          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? t.saving : t.save}
          </Button>
        </CardContent>
      </Card>

      {/* Subscription */}
      {user.subscription_status && user.subscription_status !== 'none' && (
        <Card sx={{ mb: 2, borderRadius: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">{t.subscriptionStatus}</Typography>
            <Chip
              label={user.subscription_status}
              color={user.subscription_status === 'active' ? 'success' : 'default'}
              sx={{ mt: 1, mb: 2 }}
            />
            <Box>
              <Button
                variant="outlined"
                color="warning"
                onClick={handleRefund}
                disabled={refunding || !user.stripe_customer_id}
              >
                {refunding ? t.saving : t.refund}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {user.status !== 'active' && (
              <Button variant="outlined" color="success" onClick={() => handleAction('approve')}>
                {t.approve}
              </Button>
            )}
            {user.status === 'active' && !user.is_admin && (
              <Button variant="outlined" color="warning" onClick={() => handleAction('suspend')}>
                {t.suspend}
              </Button>
            )}
            {user.status === 'suspended' && (
              <Button variant="outlined" color="success" onClick={() => handleAction('reactivate')}>
                {t.reactivate}
              </Button>
            )}
            <Button variant="outlined" onClick={() => setResetOpen(true)}>
              {t.resetPassword}
            </Button>
            {!isSelf && (
              <Button variant="outlined" color={user.is_admin ? 'warning' : 'info'}
                onClick={() => setAdminToggleOpen(true)}>
                {user.is_admin ? t.removeAdmin : t.makeAdmin}
              </Button>
            )}
            <Divider orientation="vertical" flexItem />
            <Button variant="outlined" color="error" onClick={() => setDeleteOpen(true)}>
              {t.delete}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Delete dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>{t.delete}</DialogTitle>
        <DialogContent>
          <Typography>{t.confirmDeleteUser}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>{t.cancel}</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>{t.delete}</Button>
        </DialogActions>
      </Dialog>

      {/* Password reset dialog */}
      <Dialog open={resetOpen} onClose={() => { setResetOpen(false); setNewPassword(''); }} maxWidth="xs" fullWidth>
        <DialogTitle>{t.resetPassword}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            label={t.newPassword}
            type={showPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            sx={{ mt: 1 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(p => !p)} edge="end">
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                  <IconButton onClick={() => navigator.clipboard.writeText(newPassword)} edge="end" disabled={!newPassword}>
                    <ContentCopyIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button size="small" sx={{ mt: 1 }} onClick={() => setNewPassword(generatePassword())}>
            {t.generatePassword}
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setResetOpen(false); setNewPassword(''); }}>{t.cancel}</Button>
          <Button variant="contained" onClick={handleResetPassword}
            disabled={resettingPw || newPassword.length < 8}>
            {resettingPw ? t.saving : t.save}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Admin toggle dialog */}
      <Dialog open={adminToggleOpen} onClose={() => setAdminToggleOpen(false)}>
        <DialogTitle>{user.is_admin ? t.removeAdmin : t.makeAdmin}</DialogTitle>
        <DialogContent>
          <Typography>
            {user.is_admin ? t.removeAdmin : t.makeAdmin}: {user.email}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminToggleOpen(false)}>{t.cancel}</Button>
          <Button variant="contained" color={user.is_admin ? 'warning' : 'info'}
            onClick={handleToggleAdmin} disabled={togglingAdmin}>
            {togglingAdmin ? t.saving : t.confirm}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUserDetail;
